// src/features/analytics/components/LiveAudienceCard.tsx

import { Badge, Group, Paper, Skeleton, Table, Text, Title } from '@mantine/core';
import { formatRelative } from '@/shared/utils/date';
import { AnalyticsUtils } from '../AnalyticsUtils';
import { useAnalyticsLive } from '../useAnalyticsLive';
import { useAnalyticsLiveByMedia } from '../useAnalyticsLiveByMedia';
import type { AnalyticsViewScope } from '../AnalyticsApi';

interface LiveAudienceCardProps {
  scope: AnalyticsViewScope | null;
}

/**
 * Live audience (60 s polling, mounted only on the Overview page so
 * navigating away stops polling). Account scope adds the by-media breakdown.
 */
export function LiveAudienceCard({ scope }: LiveAudienceCardProps) {
  const { live, isLoading } = useAnalyticsLive(scope);
  const accountCid = scope?.kind === 'account' ? scope.cid : null;
  const { liveByMedia } = useAnalyticsLiveByMedia(accountCid);

  const data = live?.data;
  const nowSeconds = Math.floor(Date.now() / 1000);
  const isStale =
    live?.freshness?.state === 'stale' ||
    (data?.builtAt !== undefined && AnalyticsUtils.isLiveStale(data.builtAt, nowSeconds));

  const rows = liveByMedia?.data.rows ?? [];

  return (
    <Paper withBorder p="md">
      <Group justify="space-between" align="center" mb="xs">
        <Title order={5}>Live audience</Title>
        {isStale && (
          <Badge color="yellow" variant="light">
            Stale
          </Badge>
        )}
      </Group>

      {isLoading ? (
        <Skeleton height={48} width="40%" />
      ) : !data?.hasData ? (
        <Text c="dimmed">No live viewers right now.</Text>
      ) : (
        <Group align="baseline" gap="sm">
          <Text fz={36} fw={700} lh={1}>
            {AnalyticsUtils.formatCount(data.activeSessions ?? 0)}
          </Text>
          <Text size="sm" c="dimmed">
            watching now
            {data.builtAt !== undefined && <> · as of {formatRelative(data.builtAt)}</>}
          </Text>
        </Group>
      )}

      {accountCid !== null && rows.length > 0 && (
        <Table mt="md" striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Title</Table.Th>
              <Table.Th>Stream type</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Viewers</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row) => (
              <Table.Tr key={`${row.cid}-${row.mediaId}-${row.streamType}`}>
                {/* Title decoration fallback is the display id; the wire string stays in keys */}
                <Table.Td>{row.title ?? AnalyticsUtils.displayMediaId(row.mediaId)}</Table.Td>
                <Table.Td>{row.streamType}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>
                  {AnalyticsUtils.formatCount(row.activeSessions)}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Paper>
  );
}
